import { ChangeEventHandler } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { getFromDataPath } from "../../utility";
import { PATCH_ARS_STATE, useArsContext } from "../contexts";
import { useArsTable } from "../table/ArsTableContextProvider";
import { ARST_PATCH_DATA } from "../table/interface";

export interface ArsBooleanProps {
  /** RFC 6901 JSON Pointer path to the boolean value in ArsContext state */
  dataPath: string;
  /** Optional flag to disable the control */
  disabled?: boolean;
  /** When true, a false value is treated as undefined */
  falseIsUndefined?: boolean;
  /** Unique identifier for the control */
  id: string;
  /** Label for the control */
  label: string;
  /** Optional width of the label column */
  labelWidth?: number;
  /** Optional maximum width of the control column */
  maxWidthCols?: number;
  /** Indicates if the dataPath is for ArsTable body data rather than ArsContext state */
  tableDataPath?: boolean;
}

/**
 * A boolean input control that reads from and writes to ArsContext state using a JSON Pointer path.
 * Uses PATCH_ARS_STATE action to update the state value at the specified dataPath, or
 * ARST_PATCH_DATA to update ArsTable body data when `tableDataPath` is set.
 *
 * @param props - Component props extending Control213Props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the boolean value in ArsContext state.
 * @param props.disabled - Optional flag to disable the control.
 * @param props.falseIsUndefined - Optional flag to treat false as undefined when updating state.
 * @param props.id - Unique identifier for the control.
 * @param props.label - Label for the control.
 * @param props.labelWidth - Optional width of the label column.
 * @param props.maxWidthCols - Optional maximum width of the control column.
 * @param props.tableDataPath - Optional flag to read from and write to ArsTable body data instead of ArsContext state.
 * @returns A switch control bound to ArsContext state or ArsTable body data.
 */
export const ArsBoolean = ({
  dataPath,
  disabled = false,
  falseIsUndefined = false,
  id,
  label,
  labelWidth = 2,
  maxWidthCols = 12,
  tableDataPath = false,
}: ArsBooleanProps): React.ReactElement => {
  const ars = useArsContext();
  const table = useArsTable();
  const sourceState = tableDataPath ? (table.state?.bodyData ?? {}) : ars.state;
  const value = getFromDataPath(dataPath, sourceState, (v) => typeof v === "boolean") ?? false;

  /**
   * Handles updating the boolean value in ArsContext state or ArsTable body data.
   *
   * @param e - The change event from the checkbox input.
   */
  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = e.target.checked;
    const updatedValue = falseIsUndefined && newValue === false ? undefined : newValue;
    if (tableDataPath) {
      table.dispatch({
        operation: ARST_PATCH_DATA,
        dataPath,
        value: updatedValue,
      });
    } else {
      ars.dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: updatedValue,
      });
    }
  };

  return (
    <Form.Group
      controlId={`${id}`}
      as={Row}
      className="mb-1"
    >
      <Form.Label
        column
        md={labelWidth}
      >
        {label}
      </Form.Label>
      <Col
        md={{ span: maxWidthCols - labelWidth }}
        className="d-flex align-items-center"
      >
        <Form.Check
          type="switch"
          name={id}
          checked={value}
          onChange={handleChange}
          disabled={disabled}
        />
      </Col>
    </Form.Group>
  );
};

ArsBoolean.displayName = "ArsBoolean";
